<template>
  <HeadlessListbox as="div" :disabled="disabled">
    <HeadlessListboxLabel
      v-if="label"
      class="mb-1 block text-left text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </HeadlessListboxLabel>
    <div class="relative">
      <HeadlessListboxButton
        ref="listboxButtonRef"
        :class="[
          'flex w-full flex-nowrap items-center justify-start overflow-hidden rounded-md border bg-background px-3 py-2 text-sm font-semibold focus:outline-none disabled:cursor-not-allowed disabled:bg-background-fg/3 sm:text-sm',
          hasError
            ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
            : 'focus:border-primary focus:ring-primary',
        ]"
      >
        <component
          :is="selectedOption?.icon"
          v-if="selectedOption?.icon"
          class="mr-2"
        />
        <span class="mr-2 line-clamp-1">
          {{ selectedLabel }}
        </span>
        <IconChevronDown
          class="ml-auto rounded-full text-background-fg/40"
        />
      </HeadlessListboxButton>
      <Teleport to="#listbox-options">
        <Transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <HeadlessListboxOptions
            ref="listboxOptionsRef"
            class="absolute z-10 mb-6 max-h-60 w-full overflow-y-auto rounded-lg bg-background shadow"
          >
            <HeadlessListboxOption
              v-for="option in props.options"
              :key="option.value?.toString()"
              as="template"
            >
              <li>
                <div
                  :class="[
                    modelValue === option.value
                      ? 'bg-background-fg/5'
                      : 'text-background-fg/50 hover:bg-background-fg/1',
                    'flex cursor-pointer items-center justify-start px-3 py-2',
                  ]"
                  @click="modelValue = option.value"
                >
                  <component
                    :is="option.icon"
                    v-if="option.icon"
                    class="mr-2"
                  />
                  <span
                    class="line-clamp-1 text-left text-sm font-semibold"
                  >
                    {{ option.label }}
                  </span>
                </div>
              </li>
            </HeadlessListboxOption>
          </HeadlessListboxOptions>
        </Transition>
      </Teleport>
    </div>
  </HeadlessListbox>
</template>

<script
  setup
  lang="ts"
  generic="
    TValue extends
      | string
      | number
      | null
      | boolean
      | undefined
  "
>
import { createPopper, type Modifier } from "@popperjs/core"
import type { Component } from "vue"

export type SelectOption<
  TV extends string | number | null | boolean,
> = {
  value: TV
  label: string
  icon?: Component
}

const props = defineProps<{
  options: Array<SelectOption<Exclude<TValue, undefined>>>
  disabled?: boolean
  label?: string
  hasError?: boolean
}>()

const modelValue = defineModel<TValue>({ required: true })

const selectedOption = computed(() =>
  props.options.find(
    ({ value }) => value === modelValue.value
  )
)
const selectedLabel = computed(
  () => selectedOption.value?.label || modelValue.value
)

const listboxOptionsRef = ref()
const listboxButtonRef = ref()

const sameWidthModifier: Modifier<"sameWidth", object> = {
  name: "sameWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`
  },
  effect: ({ state }) => {
    const width =
      "offsetWidth" in state.elements.reference
        ? state.elements.reference.offsetWidth
        : 0
    state.elements.popper.style.width = `${width}px`
  },
}

whenever(
  () =>
    listboxButtonRef.value?.el &&
    listboxOptionsRef.value?.el,
  () => {
    createPopper(
      listboxButtonRef.value?.el,
      listboxOptionsRef.value?.el,
      {
        placement: "bottom-start",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
          sameWidthModifier,
        ],
      }
    )
  }
)
</script>
